import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signUpSchema, type SignUpData } from '@/lib/auth/validation';
import Button from './ui/Button';
import { getStoredUtms, UTM_KEYS } from '@/lib/utm';

interface EmailSignUpFormProps {
  onSubmit: (data: SignUpData) => Promise<void>;
  isLoading: boolean;
  error?: string;
  showHowDidYouHear?: boolean;
}

const HOW_DID_YOU_HEAR_OPTIONS = [
  'Google search',
  'ChatGPT / AI assistant',
  'Instagram / TikTok',
  'A coach or teammate',
  'A friend or family member',
  'Other',
];

export default function EmailSignUpForm({ onSubmit, isLoading, error, showHowDidYouHear }: EmailSignUpFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpData>({
    resolver: zodResolver(signUpSchema),
  });

  const utms = getStoredUtms();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-utm-form="msf_v1">
      {UTM_KEYS.map((key) => (
        <input
          key={key}
          type="hidden"
          name={key}
          data-utm-field={key}
          value={utms[key]}
          readOnly
        />
      ))}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
            First Name
          </label>
          <input
            id="firstName"
            type="text"
            {...register('firstName')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
            Last Name
          </label>
          <input
            id="lastName"
            type="text"
            {...register('lastName')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email
        </label>
        <input
          id="email"
          type="email"
          {...register('email')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
          Password
        </label>
        <input
          id="password"
          type="password"
          {...register('password')}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
        {errors.password && (
          <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
        )}
      </div>

      {showHowDidYouHear && (
        <div>
          <label htmlFor="howDidYouHear" className="block text-sm font-medium text-gray-700">
            How did you hear about us? <span className="text-gray-400">(optional)</span>
          </label>
          <select
            id="howDidYouHear"
            {...register('howDidYouHear')}
            defaultValue=""
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="" disabled>
              Select one
            </option>
            {HOW_DID_YOU_HEAR_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? 'Creating account...' : 'Create account'}
      </Button>
    </form>
  );
}